import api from '@/shared/api';
import {
  buildTruthySearchParams,
  requestExcelBlobExport,
  formatDateForApi,
  type SearchFilter,
} from '@/shared/lib';

import { DOCUMENT_MANAGEMENT_EXPORT_GENERIC_MESSAGE } from '../config/messages';

export interface DocumentManagementExportExcelParams {
  searchTerm: string;
  searchType: 'name' | 'prefix';
  sort: string;
  order: string;
  filter: SearchFilter;
}

export type DocumentManagementExportQuery = {
  term?: string;
  sort?: string;
  order?: string;
  searchType?: string;
  context?: string;
  type?: string;
  revisionStatus?: string;
  date?: string;
  revision?: string | number;
  user?: string;
  branchId?: string | number;
  reviewPhase?: string;
  hasTraining?: string;
  elaborationDate?: string;
  approvalDate?: string;
  status?: string;
  stage?: string;
  effectiveDate?: string;
  requestDate?: string;
  isExternal?: string;
  physicalLocation?: string;
  format?: string;
  validFrom?: string;
  validUntil?: string;
  validityDate?: string;
  elaboratedBy?: string;
  approvedBy?: string;
  verifiedBy?: string;
  verificationDate?: string;
  documentType?: string;
};

const EXPORT_QUERY_KEYS = [
  'term',
  'sort',
  'order',
  'searchType',
  'context',
  'type',
  'revisionStatus',
  'date',
  'revision',
  'user',
  'branchId',
  'reviewPhase',
  'hasTraining',
  'elaborationDate',
  'approvalDate',
  'status',
  'stage',
  'effectiveDate',
  'requestDate',
  'isExternal',
  'physicalLocation',
  'format',
  'validFrom',
  'validUntil',
  'validityDate',
] as const satisfies readonly (keyof DocumentManagementExportQuery)[];

function buildFilterQuery(filter: SearchFilter): Partial<DocumentManagementExportQuery> {
  const acc: Partial<DocumentManagementExportQuery> = {};

  for (const [key, value] of Object.entries(filter)) {
    if (value === '' || value === null || value === undefined) {
      continue;
    }

    if (typeof value === 'number' && value === 0) {
      continue;
    }

    if (value instanceof Date) {
      acc[key as keyof DocumentManagementExportQuery] = formatDateForApi(value) as never;
    } else if (typeof value === 'boolean') {
      acc[key as keyof DocumentManagementExportQuery] = String(value) as never;
    } else {
      acc[key as keyof DocumentManagementExportQuery] = value as never;
    }
  }

  return acc;
}

export function buildDocumentManagementExportQuery(
  params: DocumentManagementExportExcelParams
): DocumentManagementExportQuery {
  const trimmed = params.searchTerm.trim();
  const filterPart = buildFilterQuery(params.filter) as DocumentManagementExportQuery;

  if (filterPart.documentType && !filterPart.type) {
    filterPart.type = String(filterPart.documentType);
  }
  delete filterPart.documentType;

  return {
    ...filterPart,
    term: trimmed.length > 0 ? trimmed : undefined,
    searchType: params.searchType,
    sort: params.sort || undefined,
    order: params.order || undefined,
    context: 'gestao',
  };
}

class DocumentManagementExportApi {
  async exportGestaoDocumentsExcel(
    params: DocumentManagementExportExcelParams
  ): Promise<{ blob: Blob; exportedCount: number; filename: string }> {
    const query = buildDocumentManagementExportQuery(params);
    const queryParams = buildTruthySearchParams(query, EXPORT_QUERY_KEYS);
    return requestExcelBlobExport(
      () =>
        api.get(`/document-revisions/search/export/excel?${queryParams.toString()}`, {
          responseType: 'blob',
        }),
      'gestao-documentos.xlsx',
      DOCUMENT_MANAGEMENT_EXPORT_GENERIC_MESSAGE
    );
  }
}

export const documentManagementExportApi = new DocumentManagementExportApi();
